// components/Dashboard.js
import React, { useEffect, useState, useMemo, useContext, useCallback } from 'react';
import '../index.css';
import { supabase } from '../supabaseClient';
import SidebarNav from "./SidebarNav";
import { AuthContext } from './AuthContext'; // Import AuthContext
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Legend, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ClipLoader } from 'react-spinners';
import { Tooltip } from 'react-tooltip';
import { FaSun, FaMoon } from 'react-icons/fa'; // For dark mode toggle
import EmployeeManagement from "./EmployeeManagement";

const Dashboard = () => {
    const [transactions, setTransactions] = useState([]);
    const [profitLoss, setProfitLoss] = useState(0);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [cashFlowPerAccount, setCashFlowPerAccount] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [transactionsPerPage, setTransactionsPerPage] = useState(10);
    const [cashFlow, setCashFlow] = useState(0);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [monthlyData, setMonthlyData] = useState([]);
    const [descriptionKeyword, setDescriptionKeyword] = useState('');
    const [loading, setLoading] = useState(false);
    const [expenseTypes, setExpenseTypes] = useState([]);
    const [selectedExpenseTypes, setSelectedExpenseTypes] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeChart, setActiveChart] = useState('profitLoss');
    const [darkMode, setDarkMode] = useState(false);
    const [chartStartDate, setChartStartDate] = useState('');
    const [chartEndDate, setChartEndDate] = useState('');
    const [selectedBankAccounts, setSelectedBankAccounts] = useState([]);

    const { userRole } = useContext(AuthContext); // Access userRole from AuthContext

    // Handle description keyword change
    const handleDescriptionKeywordChange = (e) => {
        setDescriptionKeyword(e.target.value);
        setCurrentPage(1); // Reset to first page when keyword changes
    };

    // Handle search query change
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    // Toggle dark mode
    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
        if (!darkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    };

    useEffect(() => {
        if (transactions.length > 0) {
            calculateMonthlyData();
        }
    }, [transactions, chartStartDate, chartEndDate]);

    const calculateMonthlyData = () => {
        const data = {};

        const filteredTransactions = transactions.filter((transaction) => {
            const transactionDate = new Date(transaction.date);
            if (chartStartDate && transactionDate < new Date(chartStartDate)) {
                return false;
            }
            if (chartEndDate && transactionDate > new Date(chartEndDate)) {
                return false;
            }
            return true;
        });

        filteredTransactions.forEach((transaction) => {
            const date = new Date(transaction.date);
            const month = date.getMonth(); // 0 - 11
            const year = date.getFullYear();
            const key = `${year}-${month + 1}`; // e.g., "2023-10"

            if (!data[key]) {
                data[key] = {
                    income: 0,
                    expense: 0,
                    profitLoss: 0,
                    cashFlow: 0,
                    month: key,
                };
            }

            const amount = parseFloat(transaction.amount);
            const taxAmount = parseFloat(transaction.tax_amount || 0);
            const totalAmount = transaction.total_amount;

            if (transaction.type === '收入') {
                data[key].income += amount - taxAmount;
                data[key].cashFlow += totalAmount;
            } else if (transaction.type === '支出') {
                data[key].expense += amount + taxAmount;
                data[key].cashFlow -= totalAmount;
            }

            // Calculate profit/loss
            data[key].profitLoss = data[key].income - data[key].expense;
        });

        // Convert data to array and sort
        const sortedData = Object.values(data).sort((a, b) => new Date(a.month) - new Date(b.month));

        setMonthlyData(sortedData);
    };

    useEffect(() => {
        fetchTransactions();
        fetchBankAccounts();
        fetchEmployees();
    }, []);

    // Fetch bank accounts
    const fetchBankAccounts = async () => {
        const { data, error } = await supabase
            .from('bank_accounts')
            .select('*');

        if (!error) {
            setBankAccounts(data);
        } else {
            console.error('Error fetching bank accounts:', error);
            toast.error('无法获取银行账户信息');
        }
    };

    // Fetch transactions and calculate related data
    const fetchTransactions = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('transactions')
            .select('*, bank_account_id, bank_accounts(bank_name, account_number)');

        if (!error) {
            const transactionsWithTotal = addTotalAmount(data);
            setTransactions(transactionsWithTotal);
            calculateCashFlow(transactionsWithTotal);
            calculateProfitLoss(transactionsWithTotal);
            calculateCashFlowPerAccount(transactionsWithTotal);
            extractExpenseTypes(transactionsWithTotal); // Extract expense types from updated data
        } else {
            console.error('Error fetching transactions:', error);
            toast.error('获取交易记录时出错');
        }
        setLoading(false);
    };

    // Extract unique expense types for filtering
    const extractExpenseTypes = (data) => {
        const uniqueExpenseTypes = [...new Set(data
            .filter(t => t.type === '支出' && t.transaction_type)
            .map(t => t.transaction_type))];
        setExpenseTypes(uniqueExpenseTypes);
    };

    // Fetch employees data
    const fetchEmployees = async () => {
        const { data, error } = await supabase
            .from('employees')
            .select('*');

        if (!error) {
            setEmployees(data);
        } else {
            console.error('Error fetching employees:', error);
            toast.error('无法获取员工信息');
        }
    };

    // Calculate profit/loss
    const calculateProfitLoss = useCallback((data) => {
        // Exclude internal transfers
        const filteredData = data.filter(transaction => transaction.type !== 'transfer');

        const totalIncome = filteredData
            .filter((t) => t.type === '收入')
            .reduce((sum, t) => sum + (parseFloat(t.amount) - parseFloat(t.tax_amount || 0)), 0);

        const totalExpense = filteredData
            .filter((t) => t.type === '支出')
            .reduce((sum, t) => sum + (parseFloat(t.amount) + parseFloat(t.tax_amount || 0)), 0);

        setProfitLoss(totalIncome - totalExpense);
    }, []);

    // Calculate cash flow
    const calculateCashFlow = (data) => {
        const cashFlow = data.reduce((acc, transaction) => {
            if (transaction.type === '收入') {
                return acc + transaction.total_amount;
            } else if (transaction.type === '支出') {
                return acc - transaction.total_amount;
            } else {
                return acc; // Ignore other types
            }
        }, 0);
        setCashFlow(cashFlow);
    };

    // Calculate cash flow per account
    const calculateCashFlowPerAccount = (data) => {
        const cashFlowPerAccount = {};

        data.forEach((transaction) => {
            const accountId = transaction.bank_account_id;

            if (!cashFlowPerAccount[accountId]) {
                cashFlowPerAccount[accountId] = 0;
            }

            if (transaction.type === '收入') {
                cashFlowPerAccount[accountId] += transaction.total_amount;
            } else if (transaction.type === '支出') {
                cashFlowPerAccount[accountId] -= transaction.total_amount;
            }
        });

        setCashFlowPerAccount(cashFlowPerAccount);
    };

    const addTotalAmount = (transactions) => {
        return transactions.map(transaction => {
            const amount = parseFloat(transaction.amount);
            const tax = parseFloat(transaction.tax_amount || 0);
            let total = amount;

            if (transaction.type === '收入') {
                total = amount - tax; // Assuming tax is deducted from income
            } else if (transaction.type === '支出' || transaction.type === '工资') {
                total = amount + tax; // For expenses and salary, tax is added
            }

            return { ...transaction, total_amount: total };
        });
    };

    const totalCash = useMemo(() => {
        return bankAccounts.reduce((sum, account) => sum + parseFloat(account.balance), 0);
    }, [bankAccounts]);

    // Handle start date change
    const handleStartDateChange = (e) => {
        setStartDate(e.target.value);
        setCurrentPage(1);
    };

    // Handle end date change
    const handleEndDateChange = (e) => {
        setEndDate(e.target.value);
        setCurrentPage(1);
    };

    // Pagination control
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Custom styles for react-select
    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '38px',
            borderColor: '#d1d5db',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
        }),
    };

    // Options for filters
    const typeOptions = [
        { value: '收入', label: '收入' },
        { value: '支出', label: '支出' },
        { value: '工资', label: '工资' },
    ];

    const expenseTypeOptions = expenseTypes.map((type) => ({ value: type, label: type }));

    const bankAccountOptions = bankAccounts.map((account) => ({
        value: account.id,
        label: `${account.bank_name} - ${account.account_number}`
    }));

    // Apply filters using useMemo for performance optimization
    const filteredTransactions = useMemo(() => {
        return transactions.filter((transaction) => {
            // Filter by multiple types
            if (selectedTypes.length > 0 && !selectedTypes.some(type => type.value === transaction.type)) {
                return false;
            }

            // Filter by multiple expense types
            if (transaction.type === '支出' && selectedExpenseTypes.length > 0 && !selectedExpenseTypes.some(type => type.value === transaction.transaction_type)) {
                return false;
            }

            // Filter by bank accounts
            if (selectedBankAccounts.length > 0 && !selectedBankAccounts.some(account => account.value === transaction.bank_account_id)) {
                return false;
            }

            // Date range filter
            const transactionDate = new Date(transaction.date);
            if (startDate && transactionDate < new Date(startDate)) {
                return false;
            }
            if (endDate && transactionDate > new Date(endDate)) {
                return false;
            }

            // Description keyword filter
            if (descriptionKeyword && !transaction.description.toLowerCase().includes(descriptionKeyword.toLowerCase())) {
                return false;
            }

            // Global search filter
            if (
                searchQuery &&
                !(
                    transaction.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    transaction.transaction_type?.toLowerCase().includes(searchQuery.toLowerCase())
                )
            ) {
                return false;
            }

            return true;
        });
    }, [transactions, selectedTypes, selectedExpenseTypes, selectedBankAccounts, startDate, endDate, descriptionKeyword, searchQuery]);

    // Get current page transactions
    const currentTransactions = useMemo(() => {
        const indexOfLastTransaction = currentPage * transactionsPerPage;
        const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
        return filteredTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
    }, [filteredTransactions, currentPage, transactionsPerPage]);

    const totalPages = Math.ceil(filteredTransactions.length / transactionsPerPage);

    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

    // Export data to Excel
    const exportToExcel = () => {
        const ws = XLSX.utils.json_to_sheet(filteredTransactions);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: EXCEL_TYPE });
        saveAs(data, `transactions_${new Date().toISOString()}.xlsx`);
    };

    // Handle data refresh
    const handleRefresh = () => {
        fetchTransactions();
        fetchBankAccounts();
        fetchEmployees();
        toast.success('数据已刷新');
    };

    return (
        <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {/* Sidebar */}
            <SidebarNav />

            {/* Toast Container */}
            <ToastContainer />

            {/* Main Content */}
            <main className="lg:pl-64 p-6">
                {/* Header with Refresh and Dark Mode Toggle */}
                <div className="flex justify-between items-center mb-6">
                    <button onClick={handleRefresh} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
                        刷新数据
                    </button>
                    <div className="flex items-center">
                        <button onClick={toggleDarkMode} className="p-2">
                            {darkMode ? <FaSun className="text-yellow-400" /> : <FaMoon className="text-gray-600" />}
                        </button>
                    </div>
                </div>

                {/* Financial Overview Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-100 dark:border-gray-700`}>
                        <h3 className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-2">
                            总利润/亏损
                            <span
                                data-tooltip-id="profit-tooltip"
                                data-tooltip-content="这是总收入减去总支出的结果"
                                className="ml-1 text-gray-400 cursor-pointer"
                            >
                               ℹ️</span>
                        </h3>
                        <Tooltip id="profit-tooltip" place="top" />
                        <p className={`text-2xl font-semibold ${profitLoss >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`}>
                            ZAR {profitLoss.toFixed(2)}
                        </p>
                    </div>
                    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-100 dark:border-gray-700`}>
                        <h3 className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-2">
                            现金流
                            <span
                                data-tooltip-id="cashflow-tooltip"
                                data-tooltip-content="这是所有收入减去所有支出的净现金流量"
                                className="ml-1 text-gray-400 cursor-pointer"
                            >
                               ℹ️</span>
                        </h3>
                        <Tooltip id="cashflow-tooltip" place="top" />
                        <p className={`text-2xl font-semibold ${cashFlow >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`}>
                            ZAR {cashFlow.toFixed(2)}
                        </p>
                    </div>
                    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-100 dark:border-gray-700`}>
                        <h3 className="text-gray-500 dark:text-gray-400 text-sm font-medium mb-2">总现金</h3>
                        <p className="text-2xl font-semibold text-blue-600 dark:text-blue-400">
                            ZAR {totalCash.toFixed(2)}
                        </p>
                    </div>
                </div>

                {/* Bank Accounts */}
                <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8 border border-gray-100 dark:border-gray-700`}>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">公司账户</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {bankAccounts.map((account) => (
                            <div key={account.id} className="p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
                                <p className="text-sm text-gray-600 dark:text-gray-300">{account.bank_name}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{account.account_number}</p>
                                <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                                    R {account.balance.toFixed(2)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>


                {/* Charts Section */}
                <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8 border border-gray-100 dark:border-gray-700`}>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">财务图表</h3>

                    {/* Date Range Filters for Charts */}
                    <div className="flex space-x-4 mb-4">
                        <input
                            type="date"
                            value={chartStartDate}
                            onChange={(e) => setChartStartDate(e.target.value)}
                            className="block px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                        />
                        <input
                            type="date"
                            value={chartEndDate}
                            onChange={(e) => setChartEndDate(e.target.value)}
                            className="block px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                        />
                    </div>

                    {/* Tabs for different charts */}
                    <div className="mb-6">
                        <button onClick={() => setActiveChart('profitLoss')} className={`px-4 py-2 mr-2 ${activeChart === 'profitLoss' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'} rounded-md`}>
                            利润/亏损
                        </button>
                        <button onClick={() => setActiveChart('cashFlow')} className={`px-4 py-2 mr-2 ${activeChart === 'cashFlow' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'} rounded-md`}>
                            现金流
                        </button>
                        <button onClick={() => setActiveChart('incomeExpense')} className={`px-4 py-2 ${activeChart === 'incomeExpense' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'} rounded-md`}>
                            收入 vs 支出
                        </button>
                    </div>

                    {/* Chart Display */}
                    <div style={{ width: '100%', height: 300 }}>
                        {activeChart === 'profitLoss' && (
                            <ResponsiveContainer>
                                <LineChart data={monthlyData}>
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="profitLoss" name="利润/亏损" stroke="#8884d8" />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                        {activeChart === 'cashFlow' && (
                            <ResponsiveContainer>
                                <BarChart data={monthlyData}>
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Legend />
                                    <Bar dataKey="cashFlow" name="现金流" fill="#82ca9d" />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                        {activeChart === 'incomeExpense' && (
                            <ResponsiveContainer>
                                <BarChart data={monthlyData}>
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Legend />
                                    <Bar dataKey="income" name="收入" fill="#8884d8" />
                                    <Bar dataKey="expense" name="支出" fill="#d88484" />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>


                {/* Transactions Section */}
                <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-100 dark:border-gray-700`}>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">交易记录</h3>
                    </div>

                    {/* Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                        <Select
                            isMulti
                            styles={customSelectStyles}
                            value={selectedTypes}
                            onChange={setSelectedTypes}
                            options={typeOptions}
                            placeholder="选择类型"
                        />

                        <Select
                            isMulti
                            styles={customSelectStyles}
                            value={selectedExpenseTypes}
                            onChange={setSelectedExpenseTypes}
                            options={expenseTypeOptions}
                            placeholder="选择费用类型"
                        />

                        <Select
                            isMulti
                            styles={customSelectStyles}
                            value={selectedBankAccounts}
                            onChange={setSelectedBankAccounts}
                            options={bankAccountOptions}
                            placeholder="选择银行账户"
                        />

                        <input
                            type="date"
                            value={startDate}
                            onChange={handleStartDateChange}
                            className="block w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />

                        <input
                            type="date"
                            value={endDate}
                            onChange={handleEndDateChange}
                            className="block w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />

                        <input
                            type="text"
                            value={descriptionKeyword}
                            onChange={handleDescriptionKeywordChange}
                            placeholder="搜索描述"
                            className="block w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Table */}
                    {loading ? (
                        <div className="flex justify-center items-center h-32">
                            <ClipLoader color="#2563EB" />
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">类型</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">子类型</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">不含税金额</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">税额</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">总额</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">描述</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">银行账户</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">日期</th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {currentTransactions.map((transaction) => (
                                    <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{transaction.type}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{transaction.transaction_type || 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">R {parseFloat(transaction.amount).toFixed(2)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">R {parseFloat(transaction.tax_amount || 0).toFixed(2)}</td>
                                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${transaction.type === '收入' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {transaction.type === '收入' ? '+' : '-'}R {parseFloat(transaction.total_amount).toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{transaction.description}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                            {transaction.bank_accounts ? `${transaction.bank_accounts.bank_name} - ${transaction.bank_accounts.account_number}` : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{new Date(transaction.date).toLocaleString()}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    <div className="flex items-center justify-between mt-6">
                        <div className="flex items-center">
                            <button
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                上一页
                            </button>
                            <span className="text-sm text-gray-700 dark:text-gray-300 mx-4">
                                第 {currentPage} 页，共 {totalPages} 页
                            </span>
                            <button
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                下一页
                            </button>
                        </div>
                        <div className="flex items-center">
                            <span className="text-sm text-gray-700 dark:text-gray-300 mr-2">每页显示</span>
                            <select
                                value={transactionsPerPage}
                                onChange={(e) => setTransactionsPerPage(Number(e.target.value))}
                                className="block w-20 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>
                </div>

            </main>
        </div>
    );
};

export default Dashboard;